import dayjs from "dayjs";
import { AddType, fileStatus, fileTypes, IAddStudyData } from "src/pages/Studies/models/interfaces";
import { updateFileStatus } from "../serviceHelper";

export const isEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) return true;
	if (obj1 == null || obj2 == null || typeof obj1 !== "object" || typeof obj2 !== "object") {
		return obj1 === obj2;
	}

	if (Array.isArray(obj1) && Array.isArray(obj2)) {
		if (obj1.length !== obj2.length) return false;

		return obj1.every((item, index) => {
			if (dayjs.isDayjs(item) && dayjs.isDayjs(obj2[index])) {
				return item.isSame(obj2[index]);
			}
			return isEqual(item, obj2[index]);
		});
	}

	const keys1 = Object.keys(obj1);
	for (const key of keys1) {
		if (!(key in obj2)) return false;
		const val1 = obj1[key];
		const val2 = obj2[key];

		if (val1 == null && val2 == null) continue;
		if (val1 == null || val2 == null) return false;

		if (dayjs.isDayjs(val1) && dayjs.isDayjs(val2)) {
			if (!val1.isSame(val2)) return false;
			continue;
		}
		if (typeof val1 === "object" && typeof val2 === "object") {
			if (!isEqual(val1, val2)) return false;
		} else if (val1 !== val2) {
			return false;
		}
	}

	return true;
};

export const transformObject = (detailpayload: any): IAddStudyData => {
	return {
		study_name: detailpayload?.study_name,
		study_id: detailpayload?._id || detailpayload?.study_id,
		study_type_id: detailpayload?.study_type?.study_type_id, //id
		geography_id: detailpayload?.metadata?.geography?._id ?? "", //id
		description: detailpayload?.metadata?.description,
		project_code: detailpayload?.basic_study_data?.project_code,
		custom_tags: detailpayload?.metadata?.custom_entity_tags?.map((tag: any) => tag?.drop_down_title ?? "") ?? [], //id
		start_date: detailpayload?.metadata?.start_dt ?? null,
		end_date: detailpayload?.metadata?.end_dt ?? null,
		template_id: detailpayload?.notes_config?.template?.id ?? "",
		context: detailpayload?.basic_study_data?.context,
		study_language: detailpayload?.basic_study_data?.study_language,
		taxonomy_id: detailpayload?.notes_config?.taxonomy_id,
		folder_id: detailpayload?.basic_study_data?.folder?.folder_id || null,
		process_video: detailpayload?.basic_study_data?.process_video ?? detailpayload?.process_video ?? false,
	};
};

export function extractTime(timestamp: string | number): dayjs.Dayjs {
	const totalSeconds =
		typeof timestamp === "string" && timestamp.includes(":")
			? timestamp.split(":").reduce((acc, val, idx) => {
					const num = parseFloat(val) || 0;
					// HH:MM:SS or MM:SS
					const multipliers = timestamp.split(":").length === 3 ? [3600, 60, 1] : [60, 1];
					return acc + num * (multipliers[idx] ?? 1);
				}, 0)
			: parseFloat(String(timestamp)) || 0;

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = Math.floor(totalSeconds % 60);
	const milliseconds = Math.round((totalSeconds % 1) * 1000);

	return dayjs().hour(hours).minute(minutes).second(seconds).millisecond(milliseconds);
}

export const throttleFunc = (func, delay) => {
	let throttle = false;
	return function (...args) {
		if (!throttle) {
			throttle = true;
			func.apply(this, args);
			setTimeout(() => {
				throttle = false;
			}, delay);
		}
	};
};

export function isValidTimestamp(str) {
	const timestampRegex = /^\d{13}$/;

	if (!timestampRegex.test(str)) {
		return false;
	}
	return true;
}

export const setIdsToNull = (data) => {
	return data.map((section, section_index, secArr) => ({
		...section,
		section_id: !isValidTimestamp(section?.section_id) ? section?.section_id : secArr?.length,
		sub_section: section.sub_section.map((subSection, index, subSecArr) => ({
			...subSection,
			sub_section_id: !isValidTimestamp(subSection?.sub_section_id) ? subSection?.sub_section_id : subSecArr?.length,
			questions: subSection.questions.map((question, question_index, quesArr) => ({
				...question,
				question_id: !isValidTimestamp(question?.question_id) ? question?.question_id : quesArr?.length,
			})),
		})),
	}));
};

export const setDialogIdToNull = (data) => {
	return data.map((transcript) => ({
		...transcript,
		dialog_id: !isValidTimestamp(transcript?.dialog_id) ? transcript?.dialog_id : undefined,
	}));
};

export function truncateText(text: string, maxLength: number, isDropDown = false) {
	if (text === null || text === undefined) return "";
	if (text === "") return " ";
	if (text?.length <= maxLength) {
		return text;
	}
	if (isDropDown) {
		return text?.slice(0, maxLength) + "...";
	}
	return text?.slice(0, maxLength - 3) + "..";
}

export function areAllFilesProcessed(files: any[]): boolean {
	if (!files || files?.length === 0) {
		return false; // Return false if there are no files
	}

	return files?.every((transcript) => transcript.display_on_ui === fileStatus.success);
}

export function areAnyFilesProcessed(files: any[]): boolean {
	if (!files || files?.length === 0) {
		return false; // Return false if there are no files
	}

	return files?.some((transcript) => transcript.display_on_ui === fileStatus.success);
}

export function areAnyFilesStatusComplete(files: any[]): boolean {
	if (!files || files?.length === 0) {
		return true;
	}

	return files?.some((transcript) => transcript.display_on_ui === fileStatus.success || transcript.display_on_ui === fileStatus.failed);
}

export function checkFilesLength(files: any[]): boolean {
	if (!files || files?.length === 0) {
		return true; // Return true if there are no files
	}
	return false;
}

export function checkFilesUploadingFailed(files: any[]): boolean {
	if (!files || files?.length === 0) {
		return true; // Return true if there are no files
	}

	const hasSuccessFile = files.some((file) => {
		return file?.display_on_ui === fileStatus.success;
	});

	return !hasSuccessFile;
}

export const getFileName = (fullName: string) => {
	if (!fullName) return "";
	const parts = fullName?.split(".");
	return parts?.length > 1 ? parts.slice(0, -1).join(".") : fullName;
};

export const getFileExtension = (fullName: string) => {
	if (!fullName) return "";
	const parts = fullName?.split(".");
	return parts?.length > 1 ? parts[parts?.length - 1].toLowerCase() : "";
};

export const retryUpload = async (fn, retries = 5, delay = 2000) => {
	let attempts = 0;
	while (attempts < retries) {
		try {
			return await fn();
		} catch (error) {
			attempts++;
			if (attempts === retries) {
				throw error;
			}
			// Wait for the delay before retrying
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}
};

export const updateStatusForStalledUploads = async (uploadedFiles, studyId, file_type, type) => {
	const TIME_LIMIT_IN_MS = 15 * 60 * 1000;

	if (!Array.isArray(uploadedFiles)) return;

	// Filter files that are still uploading and modified more than 10 minutes ago
	const stalledFiles = uploadedFiles.filter((file) => {
		if (!file) return false;
		if (file?.processing_status?.toLowerCase() === fileStatus.uploading && type === AddType.MANAGE_STUDY) {
			const modifiedDate = dayjs.utc(file.version_history?.modified_dt);
			const timeDifference = dayjs().diff(modifiedDate);

			// Check if the time difference is greater than 10 minutes
			return timeDifference > TIME_LIMIT_IN_MS;
		} else if (file?.status?.toLowerCase() === fileStatus.uploading && type === AddType.ADD_STUDY) {
			const modifiedDate = dayjs.utc(file.uploaded_date);
			const timeDifference = dayjs().diff(modifiedDate);

			// Check if the time difference is greater than 10 minutes
			return timeDifference > TIME_LIMIT_IN_MS;
		}
		return false;
	});

	// If there are stalled files, create a payload and call the API
	if (stalledFiles?.length > 0) {
		const fileIds = stalledFiles.map((file) => file.file_id); // Assuming file_id is within upload_details
		const payload = {
			study_id: studyId, // Make sure studyId is available in your component's scope
			file_id: fileIds,
			file_type: file_type, // Replace with the appropriate file type if needed
		};

		try {
			await updateFileStatus(payload); // Replace with your actual API call function
		} catch (error) {
			console.error("Error updating status for stalled files:", error);
		}
	}
};

export function debounce<T extends (...args: any[]) => any>(func: T, delay: number): ((...args: Parameters<T>) => void) & { cancel: () => void } {
	let timeoutId: NodeJS.Timeout | null = null;

	const debounced = (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => func(...args), delay);
	};

	debounced.cancel = () => {
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
	};

	return debounced as typeof debounced & { cancel: () => void };
}

export const processStudyElements = (processedElements: any, studyId: string, currentStudyId: string, currentElements: any) => {
	const { transcripts, reports, notes, discussion_guide } = currentElements;

	// Merge by filename so optimistic in-flight entries survive a poll that
	// the server hasn't caught up to yet. Always merge — even when the study
	// _id hasn't hydrated (currentStudyId undefined on first poll) — because
	// dropping currentElements in that branch is what made optimistic entries
	// disappear during uploads.
	const mergeByFilename = (newElements: any[], currentEls: any[]) => {
		const local = (Array.isArray(currentEls) ? currentEls : []).filter((el) => el && el?.upload_details?.filename);
		const server = (Array.isArray(newElements) ? newElements : []).filter((el) => el && el?.upload_details?.filename);
		if (server.length === 0) return local;
		if (local.length === 0) return server;

		const elementMap = new Map();
		local.forEach((element: any) => {
			elementMap.set(element.upload_details.filename, element);
		});
		server.forEach((newElement: any) => {
			const filename = newElement.upload_details.filename;
			const existingElement = elementMap.get(filename);
			elementMap.set(filename, {
				...existingElement,
				...newElement,
			});
		});

		return Array.from(elementMap.values());
	};

	return {
		...processedElements,
		transcripts: mergeByFilename(processedElements?.transcripts, transcripts),
		reports: mergeByFilename(processedElements?.reports, reports),
		discussion_guide: mergeByFilename(processedElements?.discussion_guide, discussion_guide),
		notes: processedElements?.notes || [],
	};
};

export const convertToMB = (sizeInBytes: number): string => {
	const sizeInMB = sizeInBytes / (1024 * 1024);
	return `${sizeInMB.toFixed(2)} MB`;
}
